/* Copyright (c) 2017 Backpack Health LLC */

/* global IS_ANDROID_BUILD, cordova */

import React, { FC, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import './GroupInfoActive.scss';

import { ExternalLink } from '../../../../components/TextLink';
import { GrayCard } from '../../../../components/card/Card';
import { RelativeDate } from '../../../../localization/LocalizedDate';
import GroupAboutText from '../common/GroupAboutText';
import GroupEligibilityText from '../common/GroupEligibilityText';
import GroupConsentText from '../common/GroupConsentText';
import COLORS from '../../../../styles/Colors';
import { Card } from '../../../../../bphComponents/bundle';
import GroupMetadata, { GroupMetadataType } from '../../../../../models/GroupMetadata';
import { Heading } from '../../../../components/Text';
import { SocialList, ContactList } from './GroupInfoContactInfo';
import GroupImageGallery from './GroupImageGallery';
import GroupInfoExternalLink from './GroupInfoExternalLink';
import GroupSunset from '../../../../components/GroupSunset';

import * as selectors from '../../../redux/reduxSelectors';
import { selectors as groupSelectors } from '../../../../redux/ducks/group';
import { navigate, navigateBack } from '../../../redux/NavigationActions';
import DateUtil from '../../../../../utilities/DateUtil';
import RouteNames from '../../../RouteNames';
import { TrackEvent } from '../../../../../utilities/UserMetrics';
import I18nText, { I18nImage } from '../../../../components/I18nTranslation';

const AsideContent = (props) => (
  <div style={{ paddingLeft: 16, paddingRight: 16, paddingTop: 18, paddingBottom: 18 }}>
    {props.children}
  </div>
);

function mapStateToProps(state, routerProps) {
  const profileId = selectors.getActiveProfileId(state);
  const profile = selectors.getActiveProfile(state);
  const groupId = Number(routerProps.match.params.groupId);
  const groupMetadata = groupSelectors.getGroup(state, groupId);
  const membershipData = groupSelectors.getMembership(state, profileId, groupId);

  return {
    profile,
    profileId,
    groupId,
    groupMetadata,
    membershipData,
    joinDate: DateUtil.convertTimestampToSimpleDate(membershipData.joined_at),
    onGoBack: () => {
      navigateBack(RouteNames.myGroups());
    },
    onGoToLeave: () => {
      navigate(RouteNames.leaveGroup(String(groupId)));
    },
  };
}

interface GroupInfoActiveProps {
  groupMetadata: GroupMetadataType;
  membershipData: any;
  profile: any;
  profileId: number;
  onGoToLeave(): void;
  joinDate:string;
  groupId: number;
}
interface GroupInfoDetailsProps {
  groupMetadata: GroupMetadataType;
  CardContentWrapper: any;
}
const GroupInfoDetails: FC<GroupInfoDetailsProps> = (props) => {
  const { groupMetadata, CardContentWrapper } = props;
  const group = new GroupMetadata(groupMetadata);
  const groupId = group.data.id;

  const hasContent = !!(group.imagesOrgLogo.hasValue()|| group.organizationDescription.hasValue() || group.organizationUrl.hasValue());

  return (
    <>
      <Card.CardShape>
        <CardContentWrapper>
          <GroupAboutText groupId={groupId} backgroundColor={COLORS.grayBackground} />
        </CardContentWrapper>
      </Card.CardShape>

      <Card.CardShape>
        <CardContentWrapper>
          <GroupEligibilityText groupId={groupId} />
        </CardContentWrapper>
      </Card.CardShape>
      {
        hasContent && (
          <Card.CardShape>
            <CardContentWrapper>
              <I18nImage data={group.imagesOrgLogo.get()} style={{ maxHeight: 80, maxWidth: 240 }} />

              <I18nText data={group.organizationDescription.get()} className="font-subhead font-color-gsDarkest" style={{ marginTop: 24 }} />

              <I18nText data={group.organizationUrl.get()}>
                {
                  (orgUrl) => (
                    <div style={{ marginTop: 8 }}>
                      <ExternalLink
                        href={orgUrl}
                        className="theme-group base color"
                        cutProtocols={true}
                      >
                        {orgUrl}
                      </ExternalLink>
                    </div>
                  )
                }
              </I18nText>
            </CardContentWrapper>
          </Card.CardShape>
        )
      }
    </>
  );
};

const GroupInfoActive: FC<GroupInfoActiveProps> = (props) => {
  const group = new GroupMetadata(props.groupMetadata);
  useEffect(() => {
    TrackEvent.groups.groupInfoViewed(group.groupName.getText());
  }, []);
  const hasExternalLink = group.externalLink.hasValue();
  const hasContactData = group.contact.hasValue();
  const hasNonSocialContactData = group.contact.hasNonSocialContacts();
  const hasSocialContactData = group.contact.hasSocialContacts();
  return (
    <div className="cmp-groupinfo-activegroupinfo">
      <section>
        <GroupSunset/>

        <Card.CardShape>
          <Card.CardContent>
            <div className="font-headline theme-group base color">
              <RelativeDate date={props.joinDate} precision="P1D" dateKind="medium">
                {
                  (date) => (
                    <FormattedMessage id="groupInfo.memberSince.field.label" values={{ string1: date }} />
                  )
                }
              </RelativeDate>
            </div>
            {
              group.memberCount.enabled.get() && (
                <div className="font-body font-color-gsDarkest" style={{ marginTop: 8 }}>
                  <FormattedMessage id="groupInfo.memberCount.message" values={{ string1: group.memberCount.value.get() }} />
                </div>
              )
            }
          </Card.CardContent>
        </Card.CardShape>

        {
          (hasExternalLink || hasContactData) && (
            <Card.CardShape>
              <Card.CardContent disabledPadding={['bottom']}>
                <Heading style={{ marginBottom: 24 }}>
                  <FormattedMessage id="groupInfo.contactInfo.heading" />
                </Heading>
              </Card.CardContent>

              {
                hasNonSocialContactData && (
                  <Card.CardSection hasBottomBorder={hasSocialContactData || hasExternalLink}>
                    <ContactList contact={props.groupMetadata.contact} />
                  </Card.CardSection>
                )
              }

              {
                hasSocialContactData && (
                  <Card.CardSection hasBottomBorder={hasExternalLink}>
                    <SocialList contact={props.groupMetadata.contact} style={{ paddingTop: hasNonSocialContactData ? 30 : null }} />
                  </Card.CardSection>
                )
              }

              {
                hasExternalLink && (
                  <Card.CardSection>
                    <Card.CardContent>
                      <GroupInfoExternalLink groupMetadata={props.groupMetadata} />
                    </Card.CardContent>
                  </Card.CardSection>
                )
              }
            </Card.CardShape>
          )
        }

        <div className="__about-content">
          <GroupInfoDetails {...props} CardContentWrapper={Card.CardContent} />
        </div>

        {
          group.imagesGallery.hasValue()
          && <GroupImageGallery groupData={props.groupMetadata} />
        }

        <GrayCard>
          <Card.CardContent>
            <GroupConsentText hasLeadIn={false} noHeading={false} />
          </Card.CardContent>
        </GrayCard>

        <Card.CardShape className="account-settings-logout" isFlat={true}>
          <Card.CardContent onClick={props.onGoToLeave}>
            <span className="font-body font-color-csHotRed">
              <FormattedMessage id="groupInfo.leaveGroup.button.label" />
            </span>
          </Card.CardContent>
        </Card.CardShape>
      </section>
      <aside>
        <GroupInfoDetails {...props} CardContentWrapper={AsideContent} />
      </aside>
    </div>
  );
}

export default connect(mapStateToProps)(GroupInfoActive);
